import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import { useCompanyList } from "@/modules/companies/hooks/useCompanies";
import { useProductList } from "@/modules/products/hooks/useProducts";
import { useProjectList } from "@/modules/projects/hooks/useProjects";
import type {
  ChangePasswordPayload,
  CreateUserPayload,
  PaginatedUsers,
  ResetPasswordPayload,
  RoleOption,
  UpdateMePayload,
  UpdateUserPayload,
  User,
  UserDetail,
  UserListFilters,
  UserOption,
} from "../types";

const USERS_KEY = ["users"] as const;

export function useUserList(filters: UserListFilters = {}): UseQueryResult<PaginatedUsers> {
  const { accessToken } = useAuth();
  const tokenKey = accessToken ? "auth" : "anon";
  return useQuery({
    queryKey: [...USERS_KEY, "list", tokenKey, filters],
    enabled: !!accessToken,
    queryFn: () =>
      apiFetch<PaginatedUsers>("/users", {
        token: accessToken,
        query: {
          page: filters.page ?? 1,
          limit: filters.limit ?? 20,
          search: filters.search,
          status: filters.status || undefined,
          roleId: filters.roleId,
        },
      }),
  });
}

/**
 * Minimal id+name options for assignee/owner pickers. Hits the open
 * `/users/options` endpoint (any authenticated user — no user_management gate),
 * returning only active users' { id, name }. Use this for pickers so restricted
 * roles (e.g. QA testers) get a populated assignee list; the full `useUserList`
 * stays for the admin user-management table.
 */
export function useUserOptions(): UseQueryResult<UserOption[]> {
  const { accessToken } = useAuth();
  const tokenKey = accessToken ? "auth" : "anon";
  return useQuery({
    queryKey: [...USERS_KEY, "options", tokenKey],
    enabled: !!accessToken,
    queryFn: async () => {
      const res = await apiFetch<{ options: UserOption[] }>("/users/options", {
        token: accessToken,
      });
      return res.options;
    },
  });
}

export function useUser(id: string | undefined): UseQueryResult<UserDetail> {
  const { accessToken } = useAuth();
  const tokenKey = accessToken ? "auth" : "anon";
  return useQuery({
    queryKey: [...USERS_KEY, "detail", tokenKey, id],
    enabled: Boolean(id) && !!accessToken,
    queryFn: async () => {
      const res = await apiFetch<{ user: UserDetail }>(`/users/${id}`, {
        token: accessToken,
      });
      return res.user;
    },
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  const { accessToken } = useAuth();
  return useMutation({
    mutationFn: async (payload: CreateUserPayload) => {
      const res = await apiFetch<{ user: UserDetail }>("/users", {
        method: "POST",
        body: payload,
        token: accessToken,
      });
      return res.user;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: USERS_KEY });
    },
  });
}

export function useUpdateUser(id: string) {
  const qc = useQueryClient();
  const { accessToken } = useAuth();
  return useMutation({
    mutationFn: async (payload: UpdateUserPayload) => {
      const res = await apiFetch<{ user: UserDetail }>(`/users/${id}`, {
        method: "PUT",
        body: payload,
        token: accessToken,
      });
      return res.user;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: USERS_KEY });
    },
  });
}

export function useDeleteUser() {
  const qc = useQueryClient();
  const { accessToken } = useAuth();
  return useMutation({
    mutationFn: async (id: string) =>
      apiFetch<{ success: true }>(`/users/${id}`, {
        method: "DELETE",
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: USERS_KEY });
    },
  });
}

export function useAssignRoles(id: string) {
  const qc = useQueryClient();
  const { accessToken } = useAuth();
  return useMutation({
    mutationFn: async (roleIds: string[]) => {
      const res = await apiFetch<{ user: UserDetail }>(`/users/${id}/roles`, {
        method: "POST",
        body: { roleIds },
        token: accessToken,
      });
      return res.user;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: USERS_KEY });
    },
  });
}

export function useRemoveRole(id: string) {
  const qc = useQueryClient();
  const { accessToken } = useAuth();
  return useMutation({
    mutationFn: async (roleId: string) => {
      const res = await apiFetch<{ user: UserDetail }>(
        `/users/${id}/roles/${roleId}`,
        { method: "DELETE", token: accessToken },
      );
      return res.user;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: USERS_KEY });
    },
  });
}

export function useSetUserStatus(id: string) {
  const qc = useQueryClient();
  const { accessToken } = useAuth();
  return useMutation({
    mutationFn: async (action: "suspend" | "activate") => {
      const res = await apiFetch<{ user: UserDetail }>(`/users/${id}/${action}`, {
        method: "POST",
        token: accessToken,
      });
      return res.user;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: USERS_KEY });
    },
  });
}

export type AssignmentEntity = "company" | "product" | "project";

export type GroupedProjectAssignment = {
  id: string;
  name: string;
  code: string;
  assignmentId: string;
};

export type GroupedProductAssignment = {
  id: string;
  name: string;
  code: string;
  assignmentId: string;
  companyName: string | null;
  assignedProjects: GroupedProjectAssignment[];
  hasAllProjects: boolean;
};

export type GroupedCompanyAssignment = {
  id: string;
  name: string;
  code: string;
  assignmentId: string;
  assignedProducts: GroupedProductAssignment[];
  hasAllProducts: boolean;
};

export type StandaloneProjectAssignment = {
  id: string;
  name: string;
  code: string;
  assignmentId: string;
  productName: string | null;
  companyName: string | null;
};

export type UserAssignmentsResponse = {
  companies: GroupedCompanyAssignment[];
  standaloneProducts: GroupedProductAssignment[];
  standaloneProjects: StandaloneProjectAssignment[];
};

export type UserAccessibleScope = {
  tier: 1 | 2 | 3;
  assignments: UserAssignmentsResponse;
  accessibleCompanies: { id: string; name: string; code: string }[];
  accessibleProducts: {
    id: string;
    name: string;
    code: string;
    companyName: string | null;
  }[];
  accessibleProjects: {
    id: string;
    name: string;
    code: string;
    companyName: string | null;
    productName: string | null;
  }[];
  /** Direct = explicit assignment OR cascaded from a higher direct grant.
   *  These entities give the user data access (records, documents, etc). */
  directAccessCompanyIds: string[];
  directAccessProductIds: string[];
  directAccessProjectIds: string[];
  /** Parent-only = visible only because a child is assigned. The user can
   *  see these in lists/breadcrumbs for navigation, but they grant no data
   *  access. Projects have no parent-only layer. */
  parentOnlyCompanyIds: string[];
  parentOnlyProductIds: string[];
};

export function useUserAssignments(
  userId: string,
): UseQueryResult<UserAssignmentsResponse> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: ["users", userId, "assignments", accessToken ? "auth" : "anon"],
    enabled: !!accessToken && !!userId,
    queryFn: () =>
      apiFetch<UserAssignmentsResponse>(`/users/${userId}/assignments`, {
        token: accessToken,
      }),
  });
}

export function useCreateAssignment(userId: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { entityType: AssignmentEntity; entityId: string }) =>
      apiFetch(`/users/${userId}/assignments`, {
        method: "POST",
        body: payload,
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users", userId, "assignments"] });
      qc.invalidateQueries({
        queryKey: ["users", userId, "accessible-scope"],
      });
    },
  });
}

export function useDeleteAssignment(userId: string) {
  const { accessToken } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (assignmentId: string) =>
      apiFetch(`/users/${userId}/assignments/${assignmentId}`, {
        method: "DELETE",
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users", userId, "assignments"] });
      qc.invalidateQueries({
        queryKey: ["users", userId, "accessible-scope"],
      });
    },
  });
}

// Scope-bypassing fetch hooks for the Assignments tab — admins need to see
// every operational company/product/project regardless of their own scope.

export function useCompaniesForAssignment() {
  return useCompanyList({
    page: 1,
    limit: 200,
    operationalOnly: true,
    bypassScope: true,
  });
}

export function useProductsForAssignment(companyId: string | null) {
  return useProductList({
    page: 1,
    limit: 200,
    companyId: companyId ?? undefined,
    bypassScope: true,
  });
}

export function useProjectsForAssignment(
  productId: string | null,
  companyId: string | null = null,
) {
  return useProjectList({
    page: 1,
    limit: 200,
    productId: productId ?? undefined,
    companyId: companyId ?? undefined,
    bypassScope: true,
  });
}

export function useUserAccessibleScope(
  userId: string,
  enabled = false,
): UseQueryResult<UserAccessibleScope> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [
      "users",
      userId,
      "accessible-scope",
      accessToken ? "auth" : "anon",
    ],
    enabled: !!accessToken && !!userId && enabled,
    queryFn: () =>
      apiFetch<UserAccessibleScope>(`/users/${userId}/accessible-scope`, {
        token: accessToken,
      }),
  });
}

export function useRoles(): UseQueryResult<RoleOption[]> {
  const { accessToken } = useAuth();
  const tokenKey = accessToken ? "auth" : "anon";
  return useQuery({
    queryKey: ["roles", tokenKey],
    enabled: !!accessToken,
    queryFn: async () => {
      try {
        const res = await apiFetch<{ roles: RoleOption[] }>("/roles", {
          token: accessToken,
        });
        return res.roles;
      } catch {
        return [] as RoleOption[];
      }
    },
  });
}

export type UserSummary = User;

// ─── Self-service profile hooks ─────────────────────────────

export function useMe(): UseQueryResult<UserDetail> {
  const { accessToken } = useAuth();
  return useQuery({
    queryKey: [...USERS_KEY, "me", accessToken ? "auth" : "anon"],
    enabled: !!accessToken,
    queryFn: async () => {
      const res = await apiFetch<{ user: UserDetail }>("/users/me", {
        token: accessToken,
      });
      return res.user;
    },
  });
}

export function useUpdateMe() {
  const qc = useQueryClient();
  const { accessToken } = useAuth();
  return useMutation({
    mutationFn: async (payload: UpdateMePayload) => {
      const res = await apiFetch<{ user: UserDetail }>("/users/me", {
        method: "PATCH",
        body: payload,
        token: accessToken,
      });
      return res.user;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...USERS_KEY, "me"] });
      qc.invalidateQueries({ queryKey: USERS_KEY });
    },
  });
}

export function useChangeOwnPassword() {
  const qc = useQueryClient();
  const { accessToken } = useAuth();
  return useMutation({
    mutationFn: async (payload: ChangePasswordPayload) =>
      apiFetch<{ success: true }>("/users/me/change-password", {
        method: "POST",
        body: payload,
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...USERS_KEY, "me"] });
    },
  });
}

export function useResetUserPassword(targetUserId: string) {
  const qc = useQueryClient();
  const { accessToken } = useAuth();
  return useMutation({
    mutationFn: async (payload: ResetPasswordPayload) =>
      apiFetch<{ success: true }>(`/users/${targetUserId}/reset-password`, {
        method: "POST",
        body: payload,
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: USERS_KEY });
    },
  });
}

export function useResetUserMfa(targetUserId: string) {
  const qc = useQueryClient();
  const { accessToken } = useAuth();
  return useMutation({
    mutationFn: async () =>
      apiFetch<{ success: true }>(`/users/${targetUserId}/reset-mfa`, {
        method: "POST",
        token: accessToken,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: USERS_KEY });
    },
  });
}
